import ExcelJS from "exceljs";
import { productMaterials, productCategories } from "@aclymatepackages/lists";

/**
 * Builds the customer-facing "data gaps" workbook for the products list.
 *
 * Reads the flags the BOM pipeline writes onto each material (reported /
 * assumption / insufficient) plus product-level gaps (category) and produces a
 * round-trip tracking template:
 *  - Summary tab: one readable, color-coded block per product
 *  - Gaps tab: one row per gap with editable "Your value / Unit / Notes"
 *    columns, dropdowns, locked structure, and hidden ID columns so a filled
 *    file can be mapped back to the exact material on re-import
 *  - Lists tab (hidden): dropdown sources for materials and categories
 *  - Instructions tab: the color legend
 */

const statusLabelsByKey = {
  confirmed: "Confirmed",
  requested: "Requested",
  pending: "Pending",
  incomplete: "Incomplete",
  unconfirmed: "Unconfirmed"
};

const fills = {
  missing: "FFFCEBEB",
  assumed: "FFFAEEDA",
  complete: "FFF1EFE8",
  inputHeader: "FFE6F1FB",
  header: "FFF1EFE8"
};

const solidFill = (argb) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb }
});

const roundedWeight = (weight, weightUnit) => {
  if (weight === null || weight === undefined) {
    return "—";
  }
  return `${Math.round(weight * 100) / 100} ${weightUnit || "g"}`;
};

const assumptionFieldKey = (weightSource) =>
  weightSource === "default-packaging" ? "packaging_weight" : "material_weight";

const fieldLabelsByKey = {
  category: "Category",
  material_type: "Material type",
  material_weight: "Material weight",
  packaging_weight: "Packaging weight (assumed)"
};

const gapFromMaterial = (material, componentName, componentId) => {
  const {
    type,
    label,
    flag,
    weight,
    weightUnit,
    weightSource,
    assumptionConfidence
  } = material;
  const materialName = label || type || "Unidentified material";

  if (!flag || flag === "reported") {
    return null;
  }

  if (flag === "assumption") {
    const confidenceNote = assumptionConfidence
      ? ` (${assumptionConfidence} confidence)`
      : "";
    const fieldKey = assumptionFieldKey(weightSource);
    return {
      kind: "assumed",
      component: componentName,
      componentId: componentId || "",
      fieldKey,
      field: fieldLabelsByKey[fieldKey],
      materialRef: materialName,
      detail: `${materialName}: ${roundedWeight(weight, weightUnit)}${confidenceNote}`
    };
  }

  const fieldKey = type ? "material_weight" : "material_type";
  return {
    kind: "missing",
    component: componentName,
    componentId: componentId || "",
    fieldKey,
    field: fieldLabelsByKey[fieldKey],
    materialRef: materialName,
    detail: materialName
  };
};

const gapsFromComponent = (component) =>
  (component.materials || [])
    .map((material) =>
      gapFromMaterial(material, component.name || "Component", component.id)
    )
    .filter(Boolean);

const categoryGaps = (category) =>
  category
    ? []
    : [
        {
          kind: "missing",
          component: "Product",
          componentId: "",
          fieldKey: "category",
          field: "Category",
          materialRef: "",
          detail: "Uncategorized"
        }
      ];

export const deriveProductGaps = (product) => {
  const {
    id,
    name,
    sku,
    status,
    category,
    resolvedComponents = [],
    components = [],
    packaging = []
  } = product;

  const componentList = resolvedComponents.length
    ? resolvedComponents
    : components;

  const componentGaps = componentList.flatMap(gapsFromComponent);
  const packagingGaps = packaging
    .map((material) => gapFromMaterial(material, "Packaging", ""))
    .filter(Boolean);

  return {
    id: id || "",
    name: name || "",
    sku: sku || "",
    statusKey: status,
    statusLabel: statusLabelsByKey[status] || "—",
    gaps: [...categoryGaps(category), ...componentGaps, ...packagingGaps]
  };
};

const productHeading = (name, sku) =>
  name && name !== sku ? `${name} (${sku})` : sku || name || "Untitled product";

const statusFillKey = (statusKey) => {
  if (statusKey === "incomplete") {
    return "missing";
  }
  if (statusKey === "unconfirmed") {
    return "assumed";
  }
  return "complete";
};

const addLists = (workbook) => {
  const sheet = workbook.addWorksheet("Lists", { state: "veryHidden" });
  sheet.getCell("A1").value = "Materials";
  sheet.getCell("B1").value = "Categories";
  productMaterials.forEach(({ label }, index) => {
    sheet.getCell(`A${index + 2}`).value = label;
  });
  productCategories.forEach(({ label }, index) => {
    sheet.getCell(`B${index + 2}`).value = label;
  });
  return {
    materials: `Lists!$A$2:$A$${productMaterials.length + 1}`,
    categories: `Lists!$B$2:$B$${productCategories.length + 1}`
  };
};

const addInstructions = (workbook) => {
  const sheet = workbook.addWorksheet("Instructions");
  sheet.getColumn(1).width = 4;
  sheet.getColumn(2).width = 80;
  const legend = [
    ["", "How to use this sheet"],
    [
      "",
      "Enter the corrected value in the 'Corrections' column on the Gaps tab for every red row, then send the file back."
    ],
    ["", ""],
    ["missing", "Red — missing data we need. Please provide a value."],
    [
      "assumed",
      "Amber — we filled this with an assumption. Override it if you have the real number."
    ],
    ["complete", "Grey — already complete. Locked; no action needed."]
  ];
  legend.forEach(([fillKey, text], index) => {
    const row = sheet.getRow(index + 1);
    if (fillKey && fills[fillKey]) {
      row.getCell(1).fill = solidFill(fills[fillKey]);
    }
    row.getCell(2).value = text;
    if (index === 0) {
      row.getCell(2).font = { bold: true, size: 13 };
    }
  });
};

const summaryRowsData = (derivedProducts) =>
  derivedProducts.flatMap(({ name, sku, statusKey, statusLabel, gaps }) => {
    const productRow = {
      values: [
        productHeading(name, sku),
        statusLabel,
        gaps.length
          ? `${gaps.length} issue${gaps.length === 1 ? "" : "s"}`
          : "Complete — no gaps",
        ""
      ],
      fillKey: statusFillKey(statusKey),
      isProduct: true
    };
    const gapRows = gaps.map(({ component, field, detail }) => ({
      values: [`    ↳ ${component}`, "", field, detail],
      fillKey: null,
      isProduct: false
    }));
    return [productRow, ...gapRows, { values: ["", "", "", ""], fillKey: null }];
  });

const buildSummarySheet = (workbook, derivedProducts) => {
  const sheet = workbook.addWorksheet("Summary");
  sheet.columns = [
    { header: "SKU / part", key: "a", width: 30 },
    { header: "Status", key: "b", width: 14 },
    { header: "What's missing", key: "c", width: 26 },
    { header: "Detail", key: "d", width: 46 }
  ];
  sheet.getRow(1).font = { bold: true };
  sheet.getRow(1).eachCell((cell) => {
    cell.fill = solidFill(fills.header);
  });
  sheet.views = [{ state: "frozen", ySplit: 1 }];

  summaryRowsData(derivedProducts).forEach(({ values, fillKey, isProduct }) => {
    const row = sheet.addRow(values);
    if (isProduct) {
      row.font = { bold: true };
    }
    if (fillKey && fills[fillKey]) {
      row.eachCell((cell) => {
        cell.fill = solidFill(fills[fillKey]);
      });
    }
  });
};

const gapsRowsData = (derivedProducts) =>
  derivedProducts.flatMap(({ id, name, sku, statusLabel, gaps }) =>
    gaps.length
      ? gaps.map((gap) => ({
          row: {
            sku,
            product: name,
            status: statusLabel,
            component: gap.component,
            issue: gap.kind === "assumed" ? "Assumption" : "Missing",
            field: gap.field,
            detail: gap.detail,
            yourValue: "",
            unit: "",
            notes: "",
            productId: id,
            componentId: gap.componentId,
            materialRef: gap.materialRef,
            fieldKey: gap.fieldKey
          },
          kind: gap.kind,
          fieldKey: gap.fieldKey
        }))
      : [
          {
            row: {
              sku,
              product: name,
              status: statusLabel,
              component: "",
              issue: "Complete",
              field: "",
              detail: "No gaps",
              yourValue: "",
              unit: "",
              notes: "",
              productId: id,
              componentId: "",
              materialRef: "",
              fieldKey: ""
            },
            kind: "complete",
            fieldKey: ""
          }
        ]
  );

const unlockedColumns = [8, 9, 10];

const valueValidationFor = (fieldKey, listRanges) => {
  if (fieldKey === "material_type") {
    return {
      type: "list",
      allowBlank: true,
      formulae: [listRanges.materials]
    };
  }
  if (fieldKey === "category") {
    return {
      type: "list",
      allowBlank: true,
      formulae: [listRanges.categories]
    };
  }
  return null;
};

const isWeightField = (fieldKey) =>
  fieldKey === "material_weight" || fieldKey === "packaging_weight";

const styleGapRow = (row, kind, fieldKey, listRanges) => {
  if (fills[kind]) {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = solidFill(fills[kind]);
    });
  }
  unlockedColumns.forEach((colNumber) => {
    const cell = row.getCell(colNumber);
    cell.protection = { locked: kind === "complete" };
    cell.fill = solidFill(kind === "complete" ? fills.complete : "FFFFFFFF");
  });
  if (kind === "complete") {
    return;
  }
  const valueValidation = valueValidationFor(fieldKey, listRanges);
  if (valueValidation) {
    row.getCell(8).dataValidation = {
      ...valueValidation,
      showErrorMessage: true
    };
  }
  if (isWeightField(fieldKey)) {
    row.getCell(9).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: ['"g,kg,lbs,oz,%"']
    };
  }
};

const buildGapsSheet = async (workbook, derivedProducts, listRanges) => {
  const sheet = workbook.addWorksheet("Gaps");
  sheet.columns = [
    { header: "SKU", key: "sku", width: 14 },
    { header: "Product", key: "product", width: 22 },
    { header: "Status", key: "status", width: 13 },
    { header: "Component", key: "component", width: 20 },
    { header: "Issue", key: "issue", width: 12 },
    { header: "Field needed", key: "field", width: 20 },
    { header: "Detail", key: "detail", width: 38 },
    { header: "Corrections", key: "yourValue", width: 22 },
    { header: "Unit", key: "unit", width: 8 },
    { header: "Notes", key: "notes", width: 26 },
    { header: "productId", key: "productId", width: 16, hidden: true },
    { header: "componentId", key: "componentId", width: 16, hidden: true },
    { header: "materialRef", key: "materialRef", width: 16, hidden: true },
    { header: "fieldKey", key: "fieldKey", width: 14, hidden: true }
  ];

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell, colNumber) => {
    cell.fill = solidFill(
      unlockedColumns.includes(colNumber) ? fills.inputHeader : fills.header
    );
  });
  sheet.views = [{ state: "frozen", ySplit: 1 }];

  const rows = gapsRowsData(derivedProducts);
  rows.forEach(({ row, kind, fieldKey }) => {
    const addedRow = sheet.addRow(row);
    styleGapRow(addedRow, kind, fieldKey, listRanges);
  });

  await sheet.protect("", {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatColumns: true,
    formatRows: true
  });
};

export const exportProductsGapWorkbook = async (
  products,
  { fileName = "product-data-gaps.xlsx" } = {}
) => {
  const derivedProducts = products.map(deriveProductGaps);

  const workbook = new ExcelJS.Workbook();
  addInstructions(workbook);
  buildSummarySheet(workbook, derivedProducts);
  const listRanges = addLists(workbook);
  await buildGapsSheet(workbook, derivedProducts, listRanges);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};
